export const HANDOUT_LIMITS = { count: 100, title: 120, body: 8000, pictures: 6, to: 30, pins: 100, label: 60, note: 2000 };
export const HANDOUT_KINDS = ['handout.set', 'handout.send', 'handout.take', 'handout.delete'];
export const PIN_KINDS = ['pin.set', 'pin.remove', 'pin.audience'];
export const isHandoutBody = (b) => HANDOUT_KINDS.includes(b.kind);
export const isPinBody = (b) => PIN_KINDS.includes(b.kind);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** One line: no control character at all. */
const LINE_BAD = /[\u0000-\u001f\u007f]/;
/** Paragraphs: line breaks and tabs, nothing else below space, never NUL. */
const TEXT_BAD = /[\u0000-\u0008\u000b-\u001f\u007f]/;
const isLine = (v, min, max) => typeof v === 'string' && v.trim().length >= min && v.length <= max && !LINE_BAD.test(v);
const isText = (v, min, max) => typeof v === 'string' && v.trim().length >= min && v.length <= max && !TEXT_BAD.test(v);
const isCoord = (v) => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= 1_000_000;
const isPoint = (v) => isObj(v) && isCoord(v.x) && isCoord(v.y);
const isIdList = (v, max, min = 1) => Array.isArray(v) && v.length >= min && v.length <= max && v.every(isUuid) && new Set(v).size === v.length;
const isTime = (v) => typeof v === 'string' && v.length <= 40 && !Number.isNaN(Date.parse(v));
/** Text as stored: Windows line ends read as one. */
const lines = (s) => s.replace(/\r\n?/g, '\n');
export function parseHandoutSpec(v) {
    if (!isObj(v) || !isUuid(v.id))
        return null;
    const body = typeof v.body === 'string' ? lines(v.body) : v.body;
    if (!isLine(v.title, 1, HANDOUT_LIMITS.title) || !isText(body, 0, HANDOUT_LIMITS.body))
        return null;
    if (!isIdList(v.pictures, HANDOUT_LIMITS.pictures, 0))
        return null;
    return { id: v.id, title: v.title.trim(), body: body, pictures: [...v.pictures] };
}
const parseAudience = (v) => (v === null ? null : v === 'all' ? 'all' : isIdList(v, 200, 0) ? [...v] : undefined);
export function parsePinTarget(v) {
    if (!isObj(v))
        return null;
    if ('handout' in v)
        return isUuid(v.handout) && !('note' in v) ? { handout: v.handout } : null;
    if ('note' in v) {
        const note = typeof v.note === 'string' ? lines(v.note) : v.note;
        return isText(note, 1, HANDOUT_LIMITS.note) ? { note: note } : null;
    }
    return null;
}
/** A pin from the wire or the log. `to` from the wire is never kept: the server stamps it. */
export function parsePin(v) {
    if (!isObj(v) || !isUuid(v.id) || !isPoint(v.at))
        return null;
    const label = v.label === undefined ? '' : v.label;
    if (!isLine(label, 0, HANDOUT_LIMITS.label))
        return null;
    const target = parsePinTarget(v.target);
    if (!target)
        return null;
    const to = v.to === undefined ? null : parseAudience(v.to);
    if (to === undefined)
        return null;
    return { id: v.id, at: { x: v.at.x, y: v.at.y }, label: label.trim(), target, to: 'note' in target ? null : to };
}
export function parseHandoutBody(b) {
    switch (b.kind) {
        case 'handout.set': {
            const handout = parseHandoutSpec(b.handout);
            return handout ? { kind: 'handout.set', handout } : null;
        }
        case 'handout.send': {
            if (!isUuid(b.id))
                return null;
            const to = b.to === 'all' ? 'all' : isIdList(b.to, HANDOUT_LIMITS.to) ? [...b.to] : null;
            if (!to || (b.at !== undefined && !isTime(b.at)))
                return null;
            return { kind: 'handout.send', id: b.id, to, ...(b.at !== undefined ? { at: b.at } : {}) };
        }
        case 'handout.take': {
            if (!isUuid(b.id))
                return null;
            const from = b.from === 'all' ? 'all' : isIdList(b.from, HANDOUT_LIMITS.to) ? [...b.from] : null;
            if (!from || (b.players !== undefined && !isIdList(b.players, 200, 0)))
                return null;
            return { kind: 'handout.take', id: b.id, from, ...(b.players !== undefined ? { players: [...b.players] } : {}) };
        }
        case 'handout.delete': return isUuid(b.id) ? { kind: 'handout.delete', id: b.id } : null;
        default: return null;
    }
}
export function parsePinBody(b) {
    switch (b.kind) {
        case 'pin.set': {
            const pin = parsePin(b.pin);
            return pin ? { kind: 'pin.set', pin } : null;
        }
        case 'pin.remove': return isUuid(b.id) ? { kind: 'pin.remove', id: b.id } : null;
        case 'pin.audience': {
            const to = parseAudience(b.to);
            if (!isUuid(b.handout) || to === undefined || (b.drop !== undefined && b.drop !== true))
                return null;
            return { kind: 'pin.audience', handout: b.handout, to, ...(b.drop ? { drop: true } : {}) };
        }
        default: return null;
    }
}
/** Whether a person holds what reaches this audience. */
export const audienceHas = (to, id) => to === 'all' || (Array.isArray(to) && to.includes(id));
const copyAudience = (to) => (Array.isArray(to) ? [...to] : to);
/** Handouts from an older snapshot or the log: each read through the parser. */
export function normaliseHandouts(v) {
    const out = {};
    if (!isObj(v))
        return out;
    for (const h of Object.values(v)) {
        const spec = parseHandoutSpec(h);
        if (!spec || !isObj(h))
            continue;
        const to = parseAudience(h.to);
        out[spec.id] = { ...spec, to: to ?? null, at: isTime(h.at) ? h.at : null };
    }
    return out;
}
export function normalisePins(v) {
    const out = {};
    if (!isObj(v))
        return out;
    for (const p of Object.values(v)) {
        const pin = parsePin(p);
        if (pin)
            out[pin.id] = pin;
    }
    return out;
}
/** Why the server refuses a handout event against the handouts held now, or null. */
export function handoutRefusal(handouts, body) {
    if (body.kind === 'handout.set')
        return !handouts[body.handout.id] && Object.keys(handouts).length >= HANDOUT_LIMITS.count ? `A table holds at most ${HANDOUT_LIMITS.count} handouts. Delete one first.` : null;
    return handouts[body.id] ? null : 'That handout is gone.';
}
export function reduceHandouts(handouts, body) {
    if (body.kind === 'handout.set') {
        const had = handouts[body.handout.id];
        return { ...handouts, [body.handout.id]: { ...body.handout, pictures: [...body.handout.pictures], to: had ? copyAudience(had.to) : null, at: had ? had.at : null } };
    }
    const h = handouts[body.id];
    if (!h)
        return handouts;
    if (body.kind === 'handout.delete') {
        const { [body.id]: _gone, ...rest } = handouts;
        return rest;
    }
    if (body.kind === 'handout.send') {
        const to = h.to === 'all' || body.to === 'all' ? 'all' : [...new Set([...(h.to ?? []), ...body.to])];
        return { ...handouts, [h.id]: { ...h, to, at: h.to === null ? body.at ?? h.at ?? '' : h.at } };
    }
    // Take back: from everyone, or from those named — out of "everyone", the table's players then, less those named.
    let to;
    if (body.from === 'all' || h.to === null)
        to = null;
    else {
        const from = body.from;
        to = (h.to === 'all' ? body.players ?? [] : h.to).filter((id) => !from.includes(id));
        if (!to.length)
            to = null;
    }
    return { ...handouts, [h.id]: { ...h, to, at: to === null ? null : h.at } };
}
export function reducePins(pins, body) {
    if (body.kind === 'pin.set')
        return { ...pins, [body.pin.id]: { ...body.pin, at: { ...body.pin.at }, target: { ...body.pin.target }, to: copyAudience(body.pin.to) } };
    if (body.kind === 'pin.remove') {
        if (!pins[body.id])
            return pins;
        const { [body.id]: _gone, ...rest } = pins;
        return rest;
    }
    let changed = false;
    const out = {};
    for (const [id, p] of Object.entries(pins)) {
        if (!('handout' in p.target) || p.target.handout !== body.handout) {
            out[id] = p;
            continue;
        }
        changed = true;
        if (!body.drop)
            out[id] = { ...p, to: copyAudience(body.to) };
    }
    return changed ? out : pins;
}
export const playerHandout = (h) => ({ id: h.id, title: h.title, body: h.body, pictures: [...h.pictures], at: h.at ?? '' });
/** The handouts a viewer may read: the DM every one whole; anyone else those sent to them, newest first. */
export function projectHandouts(handouts, viewer) {
    const all = Object.values(handouts);
    if (viewer.role === 'dm')
        return all.map((h) => ({ ...h, pictures: [...h.pictures], to: copyAudience(h.to) }));
    return all.filter((h) => audienceHas(h.to, viewer.id)).map(playerHandout).sort((a, b) => b.at.localeCompare(a.at));
}
/** What a player receives for a handout event: the handout, whole, when it is newly theirs or changed; its removal when no longer theirs; else nothing. */
export function projectHandoutEvent(body, before, after, viewerId) {
    const id = body.kind === 'handout.set' ? body.handout.id : body.id;
    const was = before[id];
    const now = after[id];
    const had = was && audienceHas(was.to, viewerId) ? playerHandout(was) : null;
    const has = now && audienceHas(now.to, viewerId) ? playerHandout(now) : null;
    if (has)
        return had && JSON.stringify(had) === JSON.stringify(has) ? null : { kind: 'handout.set', handout: has };
    return had ? { kind: 'handout.clear', id } : null;
}
/** Every picture a viewer may fetch because of a handout they hold (the DM: every handout's). */
export const handoutPictures = (handouts, viewer) => new Set(Object.values(handouts).filter((h) => viewer.role === 'dm' || audienceHas(h.to, viewer.id)).flatMap((h) => h.pictures));
//# sourceMappingURL=handouts.js.map